import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteActions } from '@next/core-lib/site';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { Store, select } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CropAid, FertiliserOrderplan, Product } from 'src/app/fertilisers-overview/models';

import {
  selectArticlesByCropAids,
  selectFertiliserOrderplan,
  selectNonOrderableFertilisers,
  selectProprietaryFertilisers,
} from 'src/app/fertilisers-overview/store/selectors/fertiliser.selectors';
import { SiteState } from 'src/app/shared/models';
import { selectLoggedInEmployeeAsArray } from 'src/app/store/selectors';

import * as OverviewSelectors from './../../../../store/selectors';

interface TableData {
  cropAid: string;
  productWithArticleName?: string;
  unitOfMeasurement: string;
  amount?: number;
  requisiteAmount: number;
  stockAmount: number;
  orderedAmount?: number;
  toOrderAmount: number;
  result?: number;
}

@Component({
  selector: 'app-order-receipt',
  templateUrl: './order-receipt.component.html',
  styleUrls: ['./../../../../reports/general-info/print-report.scss'],
  standalone: false,
})
export class OrderReceiptComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  protected readonly router = inject(Router);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  cropYear$ = this.store.pipe(select(OverviewSelectors.getYear));
  defaultCropYear$ = this.store.pipe(select(OverviewSelectors.selectDefaultYear));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  location$ = this.store.pipe(select(UserSelectors.selectLocation));
  fertiliserOrderplan$ = this.store.pipe(select(selectFertiliserOrderplan));
  nonOrderableFertilisers$ = this.store.pipe(select(selectNonOrderableFertilisers));
  proprietaryFertilisers$ = this.store.pipe(select(selectProprietaryFertilisers));
  cropAids$ = this.store.pipe(select(OverviewSelectors.selectCropAids));
  cropAidsWithArticles$ = this.store.select(selectArticlesByCropAids);
  pageTitle$ = combineLatest([this.cropYear$, this.defaultCropYear$, this.customer$]).pipe(
    map(
      ([cropYear, defaultCropYear, customer]) =>
        `${customer?.name ? customer.name : ''} ${this.locale.translate('createFertilizerOrderReceipt')} ${cropYear ?? defaultCropYear}`,
    ),
  );
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));
  orderableFertilisersTableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'cropAid',
        label: this.locale.translate('product'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'unitOfMeasurement',
        label: this.locale.translate('unitOfMeasurement'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'productWithArticleName',
        label: this.locale.translate('article'),
        type: TableColumnTypes.STRING,
        className: 'article-name',
        headerClassName: 'article-name',
      },
      {
        id: 'amount',
        label: this.locale.translate('fertilisers-overview.table.amount'),
        type: TableColumnTypes.NUMBER,
        digitsInfo: '1.0-0',
      },
      {
        id: 'requisiteAmount',
        label: this.locale.translate('requisite'),
        type: TableColumnTypes.NUMBER,
        digitsInfo: '1.0-0',
      },
      {
        id: 'stockAmount',
        label: this.locale.translate('productInventory'),
        type: TableColumnTypes.NUMBER,
        digitsInfo: '1.0-0',
      },
      {
        id: 'orderedAmount',
        label: this.locale.translate('fertilisers-overview.table.ordered'),
        type: TableColumnTypes.NUMBER,
        digitsInfo: '1.0-0',
      },
      {
        id: 'toOrderAmount',
        label: this.locale.translate('fertilisers-overview.table.toOrder'),
        type: TableColumnTypes.NUMBER,
        digitsInfo: '1.0-0',
      },
      {
        id: 'result',
        label: this.locale.translate('result'),
        type: TableColumnTypes.NUMBER,
        digitsInfo: '1.0-0',
      },
    ],
  };
  nonOrderableFertilisersTableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [...this.orderableFertilisersTableConfig.columns],
  };

  proprietaryFertilisersTableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [...this.orderableFertilisersTableConfig.columns],
  };
  private readonly subscription = new Subscription();

  ngOnInit() {
    this.subscription.add(
      this.pageTitle$.subscribe((pageTitle) => {
        this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
      }),
    );

    this.subscription.add(
      combineLatest([this.fertiliserOrderplan$, this.nonOrderableFertilisers$, this.proprietaryFertilisers$, this.cropAids$, this.cropAidsWithArticles$])
        .pipe(
          map(([orderableFertilisers, nonOrderableFertilisers, proprietaryFertilisers, cropAids, cropAidsWithArticles]) => {
            const orderableFertilisersDataSource: TableData[] = [];
            orderableFertilisers.forEach((fertiliser) => {
              orderableFertilisersDataSource.push({
                cropAid: fertiliser.productName,
                productWithArticleName:
                  fertiliser.cropAidWithArticles && fertiliser.cropAidWithArticles.articleNames?.length > 0
                    ? fertiliser.cropAidWithArticles.articleNames[0]?.title
                    : fertiliser.cropAidWithArticles?.name,
                unitOfMeasurement: this.getUnitOfMeasurement(fertiliser, cropAids, cropAidsWithArticles)?.toLocaleLowerCase(),
                amount: fertiliser.amount,
                requisiteAmount: fertiliser.requisiteAmount,
                stockAmount: fertiliser.stockAmount,
                orderedAmount: fertiliser.orderedAmount,
                toOrderAmount: fertiliser.toOrderAmount,
                result: fertiliser.result,
              });
            });
            this.orderableFertilisersTableConfig.dataSource.data = orderableFertilisersDataSource;

            const nonOrderableFertilisersDataSource: TableData[] = [];
            nonOrderableFertilisers.forEach((fertiliser) => {
              nonOrderableFertilisersDataSource.push({
                cropAid: fertiliser.productName,
                productWithArticleName: '',
                unitOfMeasurement: this.getUnitOfMeasurement(fertiliser, cropAids, cropAidsWithArticles)?.toLocaleLowerCase(),
                amount: null,
                requisiteAmount: fertiliser.requisiteAmount,
                stockAmount: fertiliser.stockAmount,
                toOrderAmount: fertiliser.toOrderAmount,
              });
            });
            this.nonOrderableFertilisersTableConfig.dataSource.data = nonOrderableFertilisersDataSource;

            const proprietaryFertilisersDataSource: TableData[] = [];
            proprietaryFertilisers.forEach((fertiliser) => {
              proprietaryFertilisersDataSource.push({
                cropAid: fertiliser.productName,
                productWithArticleName: '',
                unitOfMeasurement: this.getUnitOfMeasurement(fertiliser, cropAids, cropAidsWithArticles)?.toLocaleLowerCase(),
                amount: null,
                requisiteAmount: fertiliser.requisiteAmount,
                stockAmount: fertiliser.stockAmount,
                toOrderAmount: fertiliser.toOrderAmount,
              });
            });
            this.proprietaryFertilisersTableConfig.dataSource.data = proprietaryFertilisersDataSource;
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  print() {
    if (this.window) {
      this.window.print();
    }
  }

  back() {
    this.router.navigate(['/fertilizerOrders']);
  }

  private getUnitOfMeasurement(fertiliser: FertiliserOrderplan, cropAids: CropAid[], cropAidsWithArticles: Product[]) {
    const cropAidArticle = cropAidsWithArticles.find((c) => c.id === fertiliser.cropAidId);
    const cropAid = cropAids.find((c) => c.id === fertiliser.cropAidId);
    return fertiliser.cropAidUnit ?? cropAidArticle?.unitOfMeasurement ?? cropAid?.unitOfMeasurement;
  }
}
